import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, EyeOff, Store, TrendingUp, Package, Star, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const CURRENCIES = ['BNB','BTC','ETH','SOL','USDT','SUI','ASTER','HYPE','XRP'];
const CATEGORIES = ['Electronics','Fashion','Beauty','Home & Garden','Sports','Automotive','Books','Toys','Food','Health','Jewelry'];

export default function SellerDashboard() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [storeForm, setStoreForm] = useState({
    name: user?.store?.name || '',
    description: user?.store?.description || '',
    preferredCurrency: user?.store?.preferredCurrency || 'BNB',
  });
  const [productForm, setProductForm] = useState({
    title: '', description: '', category: '', priceUSD: '',
    stock: 1, displayCurrency: 'BNB', payoutCurrency: 'USDT', condition: 'new',
    shipsFrom: { country: user?.country || '', state: '', city: '' },
    shipsTo: ['worldwide'],
  });
  const [saving, setSaving] = useState(false);

  // Redirect if not seller or KYB not approved
  useEffect(() => {
    if (user?.role !== 'seller') { navigate('/'); return; }
    if (user?.kyb?.status !== 'approved') { navigate('/kyb'); return; }
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        api.get('/products/my-products'),
        api.get('/orders/my-sales'),
      ]);
      setProducts(p.data.products || []);
      setSales(s.data.orders || []);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const saveStore = async () => {
    setSaving(true);
    try {
      const res = await api.patch('/stores/setup', storeForm);
      updateUser({ store: res.data.store });
      toast.success('Store updated!');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const saveProduct = async () => {
    if (!productForm.title || !productForm.priceUSD || !productForm.category) {
      return toast.error('Fill in title, price and category');
    }
    setSaving(true);
    try {
      if (editProduct) {
        await api.put(`/products/${editProduct._id}`, productForm);
        toast.success('Product updated!');
      } else {
        await api.post('/products', productForm);
        toast.success('Product created!');
      }
      setShowForm(false);
      setEditProduct(null);
      setProductForm({ title:'', description:'', category:'', priceUSD:'', stock:1, displayCurrency:'BNB', payoutCurrency:'USDT', condition:'new', shipsFrom:{ country: user?.country||'', state:'', city:'' }, shipsTo:['worldwide'] });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to save product'); }
    finally { setSaving(false); }
  };

  const togglePublish = async (product) => {
    try {
      await api.put(`/products/${product._id}`, { isPublished: !product.isPublished });
      toast.success(product.isPublished ? 'Product unpublished' : 'Product published!');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const deleteProduct = async (id) => {
    if (!confirm('Delete this product?')) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success('Product deleted');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const openEdit = (product) => {
    setEditProduct(product);
    setProductForm({
      title: product.title, description: product.description, category: product.category,
      priceUSD: product.priceUSD, stock: product.stock, displayCurrency: product.displayCurrency, payoutCurrency: product.payoutCurrency || 'USDT',
      condition: product.condition, shipsFrom: product.shipsFrom || { country:'', state:'', city:'' },
      shipsTo: product.shipsTo || ['worldwide'],
    });
    setShowForm(true);
    setTab('products');
  };

  const totalRevenue = sales.filter(o => o.status === 'completed').reduce((s, o) => s + (o.totalUSD || 0), 0);

  const TABS = [
    { id: 'products', label: '📦 Products', count: products.length },
    { id: 'sales', label: '💸 Sales', count: sales.length },
    { id: 'store', label: '🏪 Store Setup' },
    { id: 'analytics', label: '📊 Analytics', link: '/seller/analytics' },
  ];

  return (
    <div className="min-h-screen pt-20 pb-24 px-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mt-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Sora' }}>Seller Dashboard</h1>
          <p className="text-xs text-brand-muted mt-0.5">
            {user?.store?.name || 'Set up your store'} ·{' '}
            <span style={{ color: '#00C864' }}>KYB Approved ✓</span>
          </p>
        </div>
        <button onClick={() => { setShowForm(true); setEditProduct(null); setTab('products'); }}
          className="btn-primary py-2 px-4 flex items-center gap-1.5 text-sm">
          <Plus size={15} /> Add Product
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { label: 'Products', value: products.length, color: '#FF6B00' },
          { label: 'Total Sales', value: sales.length, color: '#9945FF' },
          { label: 'Revenue', value: `$${totalRevenue.toFixed(0)}`, color: '#00C864' },
        ].map(({ label, value, color }) => (
          <div key={label} className="p-3 rounded-xl text-center" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
            <p className="text-xl font-bold" style={{ color, fontFamily: 'Sora' }}>{value}</p>
            <p className="text-xs text-brand-muted">{label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
        {TABS.map(({ id, label, count }) => (
          <button key={id} onClick={() => { setTab(id); setShowForm(false); }}
            className="flex-shrink-0 px-4 py-2 rounded-xl text-sm font-semibold transition-all"
            style={{
              background: tab === id ? 'linear-gradient(135deg,#FF6B00,#F5A623)' : '#12121A',
              color: tab === id ? 'white' : '#6B6B8A',
              border: tab === id ? 'none' : '1px solid #1E1E2E',
            }}>
            {label} {count !== undefined && <span className="ml-1 opacity-70">({count})</span>}
          </button>
        ))}
      </div>

      {/* PRODUCT FORM */}
      {showForm && (
        <div className="glass rounded-2xl p-4 mb-6">
          <h2 className="font-bold mb-4" style={{ fontFamily: 'Sora' }}>
            {editProduct ? 'Edit Product' : 'Add New Product'}
          </h2>
          <div className="space-y-3">
            <input className="input-dark" placeholder="Product Title *" value={productForm.title}
              onChange={e => setProductForm({ ...productForm, title: e.target.value })} />
            <textarea className="input-dark min-h-[80px] resize-none" placeholder="Description *"
              value={productForm.description} onChange={e => setProductForm({ ...productForm, description: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <select className="input-dark" value={productForm.category}
                onChange={e => setProductForm({ ...productForm, category: e.target.value })}>
                <option value="">Category *</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select className="input-dark" value={productForm.condition}
                onChange={e => setProductForm({ ...productForm, condition: e.target.value })}>
                <option value="new">New</option>
                <option value="used">Used</option>
                <option value="refurbished">Refurbished</option>
              </select>
              <input className="input-dark" type="number" placeholder="Price in USD *" min="0.01" step="0.01"
                value={productForm.priceUSD} onChange={e => setProductForm({ ...productForm, priceUSD: parseFloat(e.target.value) })} />
              <input className="input-dark" type="number" placeholder="Stock quantity" min="1"
                value={productForm.stock} onChange={e => setProductForm({ ...productForm, stock: parseInt(e.target.value) })} />
            </div>

            {/* Display currency — what coin buyers see the price in */}
            <div>
              <p className="text-xs text-brand-muted mb-1.5">Display price in (what buyers see)</p>
              <div className="flex flex-wrap gap-2">
                {CURRENCIES.map(c => (
                  <button key={c} type="button" onClick={() => setProductForm({ ...productForm, displayCurrency: c, payoutCurrency: 'USDT' })}
                    className="px-3 py-1 rounded-lg text-xs font-bold transition-all"
                    style={{
                      background: productForm.displayCurrency === c ? 'rgba(255,107,0,0.15)' : '#0A0A0F',
                      color: productForm.displayCurrency === c ? '#FF6B00' : '#6B6B8A',
                      border: `1px solid ${productForm.displayCurrency === c ? '#FF6B00' : '#1E1E2E'}`,
                      fontFamily: 'JetBrains Mono',
                    }}>
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* PAYOUT PREFERENCE — smart advice with dual fallback */}
            {productForm.displayCurrency && productForm.displayCurrency !== 'USDT' && (
              <div style={{ background: '#0A0A0F', border: '1px solid #1E1E2E', borderRadius: 16, padding: 16 }}>
                <p style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, marginBottom: 4 }}>
                  💰 How do you want to receive your payment?
                </p>
                <p style={{ fontSize: 11, color: '#6B6B8A', marginBottom: 14, lineHeight: 1.6 }}>
                  When a buyer pays and the escrow releases to you, choose how you want to receive your money.
                </p>

                {/* Option 1 — USDT */}
                <div
                  onClick={() => setProductForm({ ...productForm, payoutCurrency: 'USDT' })}
                  style={{
                    border: `2px solid ${productForm.payoutCurrency === 'USDT' ? '#26A17B' : '#1E1E2E'}`,
                    background: productForm.payoutCurrency === 'USDT' ? 'rgba(38,161,123,0.08)' : '#12121A',
                    borderRadius: 13, padding: 14, marginBottom: 10, cursor: 'pointer', transition: 'all 0.2s',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: 22 }}>🛡️</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: '#26A17B' }}>
                        Receive in USDT — Recommended ✅
                      </div>
                      <div style={{ fontSize: 10, color: '#6B6B8A', marginTop: 2 }}>Always stable · No market risk</div>
                    </div>
                    {productForm.payoutCurrency === 'USDT' && (
                      <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#26A17B', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'white', fontWeight: 700 }}>✓</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#E0E0F0', lineHeight: 1.7, background: 'rgba(38,161,123,0.06)', borderRadius: 8, padding: '8px 10px' }}>
                    <strong style={{ color: '#26A17B' }}>What this means:</strong> No matter what happens to {productForm.displayCurrency} price after the buyer pays — you always receive the exact USDT value. If you list a product at $50 and a buyer pays, you receive $50 worth of USDT. Even if {productForm.displayCurrency} crashes 50% the next day, your money is safe.
                    <br/><br/>
                    <strong style={{ color: '#26A17B' }}>Our advice:</strong> 💡 The crypto market goes up and down every day. Most of the time the market is unpredictable. USDT protects you completely — your earnings are always stable and ready to withdraw to your bank whenever you want.
                  </div>
                </div>

                {/* Option 2 — Coin */}
                <div
                  onClick={() => setProductForm({ ...productForm, payoutCurrency: 'COIN' })}
                  style={{
                    border: `2px solid ${productForm.payoutCurrency === 'COIN' ? '#FF6B00' : '#1E1E2E'}`,
                    background: productForm.payoutCurrency === 'COIN' ? 'rgba(255,107,0,0.06)' : '#12121A',
                    borderRadius: 13, padding: 14, marginBottom: 10, cursor: 'pointer', transition: 'all 0.2s',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <span style={{ fontSize: 22 }}>🚀</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 13, color: '#FF6B00' }}>
                        Receive in {productForm.displayCurrency} — Bull Market Option
                      </div>
                      <div style={{ fontSize: 10, color: '#6B6B8A', marginTop: 2 }}>Higher reward · Higher risk</div>
                    </div>
                    {productForm.payoutCurrency === 'COIN' && (
                      <span style={{ width: 20, height: 20, borderRadius: '50%', background: '#FF6B00', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'white', fontWeight: 700 }}>✓</span>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#E0E0F0', lineHeight: 1.7, background: 'rgba(255,107,0,0.05)', borderRadius: 8, padding: '8px 10px' }}>
                    <strong style={{ color: '#FF6B00' }}>What this means:</strong> You receive your payment in {productForm.displayCurrency} coin instead of USDT. If you receive 10 SOL today and SOL price goes up tomorrow, the value of your 10 SOL increases too. You can make extra profit just by holding.
                    <br/><br/>
                    <strong style={{ color: '#F5A623' }}>When to use this:</strong> 📈 Only choose this option during a clear <strong style={{ color: 'white' }}>bull run</strong> — when the market has been going up consistently for weeks and you believe it will continue. For example if SOL has gone from $100 to $180 in the last month and experts are saying it will hit $300, that is a good time to receive in coin.
                    <br/><br/>
                    <strong style={{ color: '#FF3232' }}>Warning:</strong> ⚠️ If the market drops after you receive your coin, your earnings lose value. Only choose this if you understand crypto markets and can afford to wait for the price to recover.
                  </div>
                </div>

                {/* Dual fallback notice */}
                <div style={{ background: 'rgba(77,162,255,0.06)', border: '1px solid rgba(77,162,255,0.25)', borderRadius: 10, padding: '10px 12px' }}>
                  <div style={{ fontSize: 11, color: '#4DA2FF', lineHeight: 1.7 }}>
                    <strong>🔒 Smart Dual Protection (always active):</strong> Regardless of which option you choose, our system tracks both the USDT value AND the coin value at the time of payment. At release time, if you chose coin payout but the coin price has dropped below your locked USDT value, we automatically pay you in USDT instead to protect you. You always get the better of the two.
                  </div>
                </div>
              </div>
            )}

            {/* Ships from */}
            <div className="grid grid-cols-3 gap-2">
              <input className="input-dark text-sm" placeholder="Country *"
                value={productForm.shipsFrom.country} onChange={e => setProductForm({ ...productForm, shipsFrom: { ...productForm.shipsFrom, country: e.target.value } })} />
              <input className="input-dark text-sm" placeholder="State"
                value={productForm.shipsFrom.state} onChange={e => setProductForm({ ...productForm, shipsFrom: { ...productForm.shipsFrom, state: e.target.value } })} />
              <input className="input-dark text-sm" placeholder="City"
                value={productForm.shipsFrom.city} onChange={e => setProductForm({ ...productForm, shipsFrom: { ...productForm.shipsFrom, city: e.target.value } })} />
            </div>

            <div className="flex gap-2">
              <button onClick={() => { setShowForm(false); setEditProduct(null); }} className="btn-outline flex-1 py-2 text-sm">Cancel</button>
              <button onClick={saveProduct} disabled={saving} className="btn-primary flex-1 py-2 flex items-center justify-center gap-2">
                {saving ? <div className="spinner" /> : editProduct ? '💾 Save Changes' : '🚀 Publish Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PRODUCTS TAB */}
      {tab === 'products' && !showForm && (
        loading ? <div className="flex justify-center py-16"><div className="spinner" /></div> :
        products.length === 0 ? (
          <div className="text-center py-20">
            <Package size={48} className="text-brand-muted mx-auto mb-4" />
            <h3 className="text-lg font-bold mb-2" style={{ fontFamily: 'Sora' }}>No products yet</h3>
            <p className="text-brand-muted mb-4">Add your first product to start selling</p>
            <button onClick={() => setShowForm(true)} className="btn-primary px-8 flex items-center gap-2 mx-auto">
              <Plus size={16} /> Add Product
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {products.map(p => (
              <div key={p._id} className="flex items-center gap-3 p-3 rounded-2xl" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
                <img src={p.images?.[0]?.url || 'https://via.placeholder.com/56/12121A/FF6B00?text=CM'}
                  alt={p.title} className="w-14 h-14 rounded-xl object-cover flex-shrink-0" style={{ border: '1px solid #1E1E2E' }} />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{p.title}</p>
                  <p className="text-xs text-brand-muted">${p.priceUSD} USD · Stock: {p.stock} · {p.category}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`badge text-xs ${p.isPublished ? 'badge-green' : 'badge-gray'}`}>
                      {p.isPublished ? 'Published' : 'Draft'}
                    </span>
                    {!p.isApproved && <span className="badge badge-red text-xs">Flagged by admin</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => togglePublish(p)} title={p.isPublished ? 'Unpublish' : 'Publish'}
                    className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-white/10">
                    {p.isPublished ? <EyeOff size={15} className="text-brand-muted" /> : <Eye size={15} className="text-green-400" />}
                  </button>
                  <button onClick={() => openEdit(p)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors">
                    <Edit size={15} className="text-brand-orange" />
                  </button>
                  <button onClick={() => deleteProduct(p._id)}
                    className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors">
                    <Trash2 size={15} className="text-red-400" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* SALES TAB */}
      {tab === 'sales' && (
        <div className="space-y-3">
          {sales.length === 0 ? (
            <div className="text-center py-20 text-brand-muted">
              <TrendingUp size={48} className="mx-auto mb-4 opacity-30" />
              <p>No sales yet — publish your products!</p>
            </div>
          ) : sales.map(order => (
            <div key={order._id} className="p-4 rounded-2xl" style={{ background: '#12121A', border: '1px solid #1E1E2E' }}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="font-mono text-sm font-bold">#{order.orderNumber}</p>
                  <p className="text-xs text-brand-muted">Buyer: {order.buyer?.username} · {order.buyer?.country}</p>
                </div>
                <span className={`badge text-xs ${order.status === 'completed' ? 'badge-green' : order.status === 'disputed' ? 'badge-red' : 'badge-yellow'}`}>
                  {order.status}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-brand-muted">{order.items?.length} item(s)</span>
                <span className="font-bold font-mono" style={{ color: '#F7931A' }}>
                  {order.cryptoAmount?.toFixed(6)} {order.paymentCurrency}
                </span>
              </div>
              <div className="mt-2 text-xs" style={{ color: order.escrow?.status === 'released' ? '#00C864' : '#F5A623' }}>
                Escrow: {order.escrow?.status?.replace('_', ' ').toUpperCase()}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* STORE SETUP TAB */}
      {tab === 'store' && (
        <div className="glass rounded-2xl p-4 space-y-4">
          <h2 className="font-bold text-lg" style={{ fontFamily: 'Sora' }}>Store Settings</h2>
          <div>
            <label className="block text-xs text-brand-muted mb-1">Store Name</label>
            <input className="input-dark" placeholder="Your store name" value={storeForm.name}
              onChange={e => setStoreForm({ ...storeForm, name: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs text-brand-muted mb-1">Store Description</label>
            <textarea className="input-dark min-h-[80px] resize-none" placeholder="Tell buyers about your store"
              value={storeForm.description} onChange={e => setStoreForm({ ...storeForm, description: e.target.value })} />
          </div>
          <div>
            <label className="block text-xs text-brand-muted mb-1">Default Price Currency</label>
            <div className="flex flex-wrap gap-2">
              {CURRENCIES.map(c => (
                <button key={c} type="button" onClick={() => setStoreForm({ ...storeForm, preferredCurrency: c })}
                  className="px-3 py-1 rounded-lg text-xs font-bold transition-all"
                  style={{
                    background: storeForm.preferredCurrency === c ? 'rgba(255,107,0,0.15)' : '#0A0A0F',
                    color: storeForm.preferredCurrency === c ? '#FF6B00' : '#6B6B8A',
                    border: `1px solid ${storeForm.preferredCurrency === c ? '#FF6B00' : '#1E1E2E'}`,
                    fontFamily: 'JetBrains Mono',
                  }}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          <button onClick={saveStore} disabled={saving} className="btn-primary w-full flex items-center justify-center gap-2">
            {saving ? <div className="spinner" /> : '💾 Save Store Settings'}
          </button>

          {user?.store?.name && (
            <>
              <Link to='/commission' className='flex items-center justify-center gap-2 py-2 text-sm text-brand-muted hover:text-white mb-2'>View Commission Rates & Premium Plans</Link>
              <Link to={`/store/${user.username}`}
                className="flex items-center justify-center gap-2 py-2 text-sm text-brand-orange hover:underline">
                <Store size={14} /> View My Public Store
              </Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}
